import { localized, msg } from '@lit/localize';
import { css, html, LitElement, type PropertyValues } from 'lit';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-edit.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-visibility.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-visibility-off.js';
import {
  isRuntimeValueDraftDirty,
  normalizeRuntimeValueKey,
  type RuntimeValueDraft,
  validateRuntimeValueDraft,
} from './property-table-draft-utils';

const MASKED_SECRET_VALUE = '********';

type PropertyTextField = EventTarget & {
  value: string;
};

export interface PropertyItem {
  id: string;
  key: string;
  value?: string;
  type?: string;
  kind?: 'config' | 'secret';
  hasValue?: boolean;
}

@localized()
export class SbPropertyTable extends LitElement {
  static properties = {
    addLabel: { type: String, attribute: 'add-label' },
    data: { type: Array },
    reservedKeys: { type: Array, attribute: false },
    secretValues: { type: Boolean, attribute: 'secret-values' },
    revealedIds: { attribute: false },
  };

  declare addLabel: string;
  declare data: PropertyItem[];
  declare reservedKeys: string[];
  declare secretValues: boolean;
  declare revealedIds: Set<string>;

  constructor() {
    super();
    this.addLabel = '';
    this.data = [];
    this.reservedKeys = [];
    this.secretValues = false;
    this.revealedIds = new Set();
  }

  private newDraft: RuntimeValueDraft | null = null;

  private rowDrafts: Record<string, RuntimeValueDraft> = {};

  static styles = css`
    .property-header {
      display: flex;
      justify-content: flex-start;
      margin: 2px 0 6px;
    }
    .property-header sp-action-button {
      height: 28px;
      min-width: 0;
      --mod-actionbutton-icon-size: 12px;
      --mod-actionbutton-padding-inline: 6px;
    }
    .property-table {
      display: flex;
      flex-direction: column;
      gap: 0;
      width: 100%;
    }
    .property-entry {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 8px;
      align-items: center;
      min-width: 0;
      padding: 8px 0;
      border-top: 1px solid var(--spectrum-global-color-gray-300, #d0d0d0);
    }
    .property-entry:first-child {
      border-top: 0;
    }
    .property-form {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin: 2px 0 8px;
      padding: 8px;
      border: 1px solid var(--spectrum-global-color-gray-300, #d0d0d0);
      border-radius: 6px;
      background: var(--inspector-control-bg, transparent);
    }
    .property-entry-main {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }
    .property-key {
      min-width: 0;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.25;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .property-value {
      min-width: 0;
      color: var(--spectrum-gray-800);
      font-size: 12px;
      line-height: 1.35;
      overflow-wrap: anywhere;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .property-value--empty {
      color: var(--spectrum-gray-600);
      font-style: italic;
    }
    .property-value--masked {
      letter-spacing: 1px;
    }
    .property-field {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }
    .property-label {
      font-size: 12px;
      font-weight: 600;
      color: var(--spectrum-gray-700);
    }
    sp-textfield {
      width: 100%;
      min-width: 0;
      padding: 0 !important;
      margin: 0 !important;
      font-size: 0.9em;
      box-sizing: border-box;
    }
    .property-actions {
      display: flex;
      gap: 4px;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
    }
    .property-row-actions {
      opacity: 0.72;
      flex-wrap: nowrap;
    }
    .property-entry:hover .property-row-actions,
    .property-entry:focus-within .property-row-actions {
      opacity: 1;
    }
    .property-row-actions sp-action-button,
    .property-row-actions .secret-toggle-button {
      width: 28px;
      min-width: 28px;
      height: 28px;
      --mod-actionbutton-icon-size: 13px;
      --mod-actionbutton-padding-inline: 0px;
    }
    .property-form-actions sp-action-button,
    .property-form-actions .secret-toggle-button {
      flex: 0 0 auto;
      min-width: 44px;
      height: 28px;
      --mod-actionbutton-padding-inline: 6px;
    }
    .property-form-actions .secret-toggle-button {
      width: 28px;
      min-width: 28px;
      --mod-actionbutton-icon-size: 13px;
      --mod-actionbutton-padding-inline: 0px;
    }
    .property-row-error {
      font-size: 12px;
      color: var(--spectrum-global-color-red-600, #d7373f);
    }
  `;

  protected override updated(changed: PropertyValues<this>) {
    if (changed.has('data')) {
      this.clearCompletedDrafts();
    }
  }

  private get resolvedAddLabel(): string {
    return (
      this.addLabel ||
      (this.secretValues ? msg('Add secret value') : msg('Add config entry'))
    );
  }

  private createDraft(item?: PropertyItem): RuntimeValueDraft {
    return {
      id: item?.id ?? crypto.randomUUID(),
      key: item?.key ?? '',
      value: item?.value ?? '',
      valueTouched: false,
    };
  }

  private onDraftKeyInput(draftId: string, item: PropertyItem, e: Event) {
    const target = e.target as PropertyTextField;
    const draft = this.rowDrafts[draftId] ?? this.createDraft(item);
    this.rowDrafts = {
      ...this.rowDrafts,
      [draftId]: { ...draft, key: target.value },
    };
    this.requestUpdate();
  }

  private onDraftValueInput(draftId: string, item: PropertyItem, e: Event) {
    const target = e.target as PropertyTextField;
    const draft = this.rowDrafts[draftId] ?? this.createDraft(item);
    this.rowDrafts = {
      ...this.rowDrafts,
      [draftId]: {
        ...draft,
        value: target.value,
        valueTouched: true,
      },
    };
    this.requestUpdate();
  }

  private onNewDraftKeyInput(e: Event) {
    if (!this.newDraft) {
      return;
    }
    const target = e.target as PropertyTextField;
    this.newDraft = { ...this.newDraft, key: target.value };
    this.requestUpdate();
  }

  private onNewDraftValueInput(e: Event) {
    if (!this.newDraft) {
      return;
    }
    const target = e.target as PropertyTextField;
    this.newDraft = {
      ...this.newDraft,
      value: target.value,
      valueTouched: true,
    };
    this.requestUpdate();
  }

  private onNewDraftValueKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.commitNewDraft();
    }
  }

  private onDraftValueKeydown(item: PropertyItem, e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.commitDraft(item);
    }
  }

  private startNewDraft() {
    if (this.newDraft) {
      return;
    }
    this.newDraft = this.createDraft();
    this.requestUpdate();
  }

  private cancelNewDraft() {
    this.newDraft = null;
    this.requestUpdate();
  }

  private startDraft(item: PropertyItem) {
    this.rowDrafts = {
      ...this.rowDrafts,
      [item.id]: this.rowDrafts[item.id] ?? this.createDraft(item),
    };
    this.requestUpdate();
  }

  private cancelDraft(item: PropertyItem) {
    const { [item.id]: _removed, ...rest } = this.rowDrafts;
    this.rowDrafts = rest;
    this.requestUpdate();
  }

  private commitNewDraft() {
    if (
      !this.newDraft ||
      this.validationError(this.newDraft, this.newDraft.id)
    ) {
      return;
    }

    this.dispatchInsert({
      id: this.newDraft.id,
      key: this.normalizeKey(this.newDraft.key),
      value: this.newDraft.value,
      type: '',
      kind: this.secretValues ? 'secret' : 'config',
    });
    this.requestUpdate();
  }

  private commitDraft(item: PropertyItem) {
    const draft = this.rowDrafts[item.id];
    if (
      !draft ||
      !this.isDirty(item, draft) ||
      this.validationError(draft, item.id)
    ) {
      return;
    }

    this.dispatchChange({
      ...item,
      key: this.normalizeKey(draft.key),
      value: draft.valueTouched ? draft.value : item.value,
    });
  }

  private dispatchRemove(item: PropertyItem) {
    this.cancelDraft(item);
    this.dispatchRemoved(item);
  }

  private validationError(
    draft: RuntimeValueDraft,
    currentId?: string
  ): string {
    return validateRuntimeValueDraft(draft, this.data, {
      currentId,
      newDraft: this.newDraft,
      reservedKeys: this.reservedKeys,
    });
  }

  private normalizeKey(key: string): string {
    return normalizeRuntimeValueKey(key);
  }

  private isDirty(item: PropertyItem, draft: RuntimeValueDraft): boolean {
    return isRuntimeValueDraftDirty(item, draft);
  }

  private clearCompletedDrafts() {
    let changed = false;
    if (
      this.newDraft &&
      this.data.some((item) => item.id === this.newDraft?.id)
    ) {
      this.newDraft = null;
      changed = true;
    }

    const nextDrafts: Record<string, RuntimeValueDraft> = {};
    for (const [id, draft] of Object.entries(this.rowDrafts)) {
      const item = this.data.find((value) => value.id === id);
      if (!item) {
        changed = true;
        continue;
      }
      if (this.isDirty(item, draft)) {
        nextDrafts[id] = draft;
      } else {
        changed = true;
      }
    }
    if (
      changed ||
      Object.keys(nextDrafts).length !== Object.keys(this.rowDrafts).length
    ) {
      this.rowDrafts = nextDrafts;
      this.requestUpdate();
    }
  }

  private dispatchChange(item: PropertyItem) {
    this.dispatchEvent(new CustomEvent('data-changed', { detail: item }));
  }
  private dispatchInsert(item: PropertyItem) {
    this.dispatchEvent(new CustomEvent('data-added', { detail: item }));
  }
  private dispatchRemoved(item: PropertyItem) {
    this.dispatchEvent(new CustomEvent('data-removed', { detail: item }));
  }
  private dispatchSecretRevealToggle(item: PropertyItem, e: Event) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('secret-reveal-toggled', { detail: item })
    );
  }

  private renderNewDraftRow(draft: RuntimeValueDraft) {
    const error = this.validationError(draft, draft.id);
    return html`
      <div class="property-form">
        <div class="property-field">
          <span class="property-label">${msg('Key')}</span>
          <sp-textfield
            autocomplete="off"
            label=${msg('New runtime value key')}
            name="workflow-runtime-entry-new-key"
            .value=${draft.key}
            @input=${(e: Event) => this.onNewDraftKeyInput(e)}
          ></sp-textfield>
        </div>
        <div class="property-field">
          <span class="property-label">${msg('Value')}</span>
          <sp-textfield
            autocomplete="off"
            label=${msg('New runtime value')}
            name="workflow-runtime-entry-new-value"
            type="text"
            .value=${draft.value}
            @input=${(e: Event) => this.onNewDraftValueInput(e)}
            @keydown=${(e: KeyboardEvent) => this.onNewDraftValueKeydown(e)}
          ></sp-textfield>
        </div>
        <div class="property-actions property-form-actions">
          <sp-action-button
            label=${this.secretValues ? msg('Add secret value') : msg('Add config entry')}
            ?disabled=${Boolean(error)}
            @click=${() => this.commitNewDraft()}
          >
            ${msg('Add')}
          </sp-action-button>
          <sp-action-button
            quiet
            label=${msg('Cancel')}
            @click=${() => this.cancelNewDraft()}
          >
            ${msg('Cancel')}
          </sp-action-button>
        </div>
        ${error ? html`<div class="property-row-error">${error}</div>` : null}
      </div>
    `;
  }

  private renderEditRow(item: PropertyItem, draft: RuntimeValueDraft) {
    const dirty = this.isDirty(item, draft);
    const error = this.validationError(draft, item.id);
    const revealed = this.revealedIds.has(item.id);
    const hiddenSecret =
      this.secretValues && !revealed && item.hasValue === true;
    const hasSecretValue = Boolean(item.hasValue || item.value);

    return html`
      <div class="property-form">
        <div class="property-field">
          <span class="property-label">${msg('Key')}</span>
          <sp-textfield
            autocomplete="off"
            label=${msg('Runtime value key')}
            name="workflow-runtime-entry-key"
            .value=${draft.key}
            @input=${(e: Event) => this.onDraftKeyInput(item.id, item, e)}
          ></sp-textfield>
        </div>
        <div class="property-field">
          <span class="property-label">${msg('Value')}</span>
          <sp-textfield
            autocomplete="off"
            label=${msg('Runtime value')}
            name="workflow-runtime-entry-value"
            type="text"
            .value=${draft.value}
            placeholder=${
              hiddenSecret && hasSecretValue
                ? msg('Leave blank to keep current value')
                : ''
            }
            @input=${(e: Event) => this.onDraftValueInput(item.id, item, e)}
            @keydown=${(e: KeyboardEvent) => this.onDraftValueKeydown(item, e)}
          ></sp-textfield>
        </div>
        <div class="property-actions property-form-actions">
          ${
            this.secretValues && hasSecretValue
              ? html`<sp-action-button
                  quiet
                  class="secret-toggle-button"
                  label=${revealed ? msg('Hide secret value') : msg('Show secret value')}
                  @click=${(e: Event) =>
                    this.dispatchSecretRevealToggle(item, e)}
                >
                  ${
                    revealed
                      ? html`<sp-icon-visibility-off
                          slot="icon"
                        ></sp-icon-visibility-off>`
                      : html`<sp-icon-visibility
                          slot="icon"
                        ></sp-icon-visibility>`
                  }
                </sp-action-button>`
              : null
          }
          <sp-action-button
            label=${msg('Save')}
            ?disabled=${Boolean(error) || !dirty}
            @click=${() => this.commitDraft(item)}
          >
            ${msg('Save')}
          </sp-action-button>
          <sp-action-button
            quiet
            label=${msg('Cancel')}
            @click=${() => this.cancelDraft(item)}
          >
            ${msg('Cancel')}
          </sp-action-button>
          <sp-action-button
            quiet
            label=${msg('Remove')}
            @click=${() => this.dispatchRemove(item)}
          >
            ${msg('Remove')}
          </sp-action-button>
        </div>
        ${error ? html`<div class="property-row-error">${error}</div>` : null}
      </div>
    `;
  }

  private renderSavedRow(item: PropertyItem) {
    const draft = this.rowDrafts[item.id];
    if (draft) {
      return this.renderEditRow(item, draft);
    }

    const revealed = this.revealedIds.has(item.id);
    const hiddenSecret =
      this.secretValues && !revealed && item.hasValue === true;
    const hasSecretValue = Boolean(item.hasValue || item.value);
    const value =
      hiddenSecret && hasSecretValue ? MASKED_SECRET_VALUE : (item.value ?? '');
    const displayValue = value || msg('No value');

    return html`
      <div class="property-entry">
        <div class="property-entry-main">
          <div class="property-key" title=${item.key}>${item.key}</div>
          <div
            class=${`property-value${value ? '' : ' property-value--empty'}${
              hiddenSecret && hasSecretValue ? ' property-value--masked' : ''
            }`}
            title=${displayValue}
          >${displayValue}</div>
        </div>
        <div class="property-actions property-row-actions">
          ${
            this.secretValues && hasSecretValue
              ? html`<sp-action-button
                  quiet
                  class="secret-toggle-button"
                  label=${revealed ? msg('Hide secret value') : msg('Show secret value')}
                  @click=${(e: Event) =>
                    this.dispatchSecretRevealToggle(item, e)}
                >
                  ${
                    revealed
                      ? html`<sp-icon-visibility-off
                          slot="icon"
                        ></sp-icon-visibility-off>`
                      : html`<sp-icon-visibility
                          slot="icon"
                        ></sp-icon-visibility>`
                  }
                </sp-action-button>`
              : null
          }
          <sp-action-button
            quiet
            label=${msg('Edit')}
            @click=${() => this.startDraft(item)}
          >
            <sp-icon-edit slot="icon"></sp-icon-edit>
          </sp-action-button>
          <sp-action-button
            quiet
            label=${msg('Remove')}
            @click=${() => this.dispatchRemove(item)}
          >
            <sp-icon-delete slot="icon"></sp-icon-delete>
          </sp-action-button>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="property-header">
        <sp-action-button
          label=${this.resolvedAddLabel}
          ?disabled=${Boolean(this.newDraft)}
          @click=${() => this.startNewDraft()}
        >
          <sp-icon-add slot="icon"></sp-icon-add>
          ${this.resolvedAddLabel}
        </sp-action-button>
      </div>
      <div class="property-table">
        ${this.newDraft ? this.renderNewDraftRow(this.newDraft) : null}
        ${this.data.map((item) => this.renderSavedRow(item))}
      </div>
    `;
  }
}

customElements.define('sb-property-table', SbPropertyTable);
